import { v4 as uuidv4 } from 'uuid';

import BaseDataObject from './BaseDataObject';
import BaseReduction from './BaseReduction';

// ---------------------------------------- JockeyReduction ----------------------------------------

export default class JockeyTrainerReduction extends BaseReduction {
  initialize(groupsKey, groupRowClass) {
    this.groupsKey = groupsKey;
    this.groupRowClass = groupRowClass;
    this.ensureArray(this.groupsKey);
  }

  isEmpty() {
    const rows = this.getJockeyTrainerGroupRows();
    return rows.every(row => row.isEmpty());
  }

  getJockeyTrainerGroupRows() {
    return this.getObjects(this.groupsKey, this.groupRowClass);
  }

  getJockeyTrainerGroupRow(index) {
    return this.getObject(this.groupsKey, this.groupRowClass, index);
  }

  getNumberOfJockeyTrainerGroupRows() {
    return this.getNumberOf(this.groupsKey);
  }

  addJockeyTrainerGroupRow() {
    this.invalidate();
    return this.addObject(this.groupsKey, this.groupRowClass);
  }

  deleteJockeyTrainerGroupRow(uid) {
    this.invalidate();
    const numberOfGroupRows = this.getNumberOfJockeyTrainerGroupRows();
    const groupRows = this.getJockeyTrainerGroupRows();
    const isUUID =
      /^[\da-f]{8}-[\da-f]{4}-[1-5][\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i;
    const uniqId = isUUID.test(uid) ? uid : Number.parseInt(uid, 10);
    for (let index = 0; index < numberOfGroupRows; index++) {
      if (groupRows[index].getUUID() === uniqId) {
        this.deleteObject(this.groupsKey, index);
        return;
      }
    }
  }
}

// ---------------------------------------- JockeyReductionGroupRow ----------------------------------------
export class JockeyTrainerReductionGroupRow extends BaseDataObject {
  constructor() {
    super(...arguments);
    this.ensureInteger('id', -1);
    this.ensureInteger('minNumberOfRaces', -1);
    this.ensureInteger('maxNumberOfRaces', -1);
    this.ensureInteger('winPercentMin', 0);
    this.ensureInteger('winPercentMax', 0);
    this.ensureString('type', '');
    this.ensureInteger('uuid', uuidv4());
  }

  initialize(rowsKey, jockeyTrainerAbstractionClass) {
    this.rowsKey = rowsKey;
    this.jockeyTrainerAbstractionClass = jockeyTrainerAbstractionClass;
    this.ensureArray(this.rowsKey);
  }

  isEmpty() {
    if (this.getMinNumberOfRaces() < 0 && this.getMaxNumberOfRaces() < 0) {
      return true;
    } else if (this.getGroupType() === '') {
      return true;
    } else if (this.getJockeyTrainerRows().length === 0) {
      return true;
    } else {
      return false;
    }
  }

  getJockeyTrainerRows() {
    return this.getObjects(this.rowsKey, this.jockeyTrainerAbstractionClass);
  }

  getMinNumberOfRaces() {
    return this.getInteger('minNumberOfRaces', -1);
  }

  setMinNumberOfRaces(v) {
    return this.setInteger('minNumberOfRaces', v);
  }

  getMaxNumberOfRaces() {
    return this.getInteger('maxNumberOfRaces', -1);
  }

  setMaxNumberOfRaces(v) {
    return this.setInteger('maxNumberOfRaces', v);
  }

  getWinPercentMin() {
    return this.getInteger('winPercentMin', 0);
  }
  getWinPercentMax() {
    return this.getInteger('winPercentMax', 0);
  }

  setWinPercentMin(v) {
    return this.setInteger('winPercentMin', v);
  }

  setWinPercentMax(v) {
    return this.setInteger('winPercentMax', v);
  }

  getGroupType() {
    return this.getString('type', '');
  }

  setGroupType(v) {
    return this.setString('type', v);
  }

  jockeyTrainerExists(v) {
    const id = v.jockey ?? v.trainer;
    const jockeyTrainers = this.getJockeyTrainers();
    return jockeyTrainers.some(item => item.getId() === id);
  }

  addJockeyTrainers(v) {
    this.invalidate();
    if (!this.jockeyTrainerExists(v)) {
      return this.addObject(
        this.rowsKey,
        this.jockeyTrainerAbstractionClass,
        v,
      );
    }
  }

  removeJockeyTrainer(v) {
    this.invalidate();
    const jockeyTrainers = this.getObjects(
      this.rowsKey,
      this.jockeyTrainerAbstractionClass,
    );
    const jockeyTrainerIndex = jockeyTrainers.findIndex(
      item => item.getId() === v,
    );
    if (jockeyTrainerIndex !== -1) {
      this.deleteObject(this.rowsKey, jockeyTrainerIndex);
    }
  }

  removeAllJockeyTrainers() {
    this.invalidate();
    this.deleteAllObjects(this.rowsKey);
  }

  getJockeyTrainers() {
    return this.getObjects(this.rowsKey, this.jockeyTrainerAbstractionClass);
  }

  getNumberOfJockeyTrainerRows() {
    return this.getNumberOf(this.rowsKey);
  }

  getJockeyTrainerStarts() {
    const jockeyTrainers = this.getJockeyTrainers();
    return jockeyTrainers
      .map(item => item.getStarts())
      .reduce((prev, curr) => [...prev, ...curr]);
  }

  getId() {
    return this.getInteger('id');
  }

  getUUID() {
    return this.getString('uuid', '');
  }
}
